import { createSlice, createSelector } from '@reduxjs/toolkit';
import { isWeb } from '@global/screens';
import { setSavedFilter } from '@global/helpers';

const defaultLimit = isWeb ? 10 : 15;

const createDefaultParams = () => ({
  filter: {},
  skip: 0,
  limit: defaultLimit,
  fields: '',
  sort: '',
});

const initialState = {
  ...createDefaultParams(), // old root behaviour
  scoped: {}, // new scoped behaviour
};

const rootParamsSelectorCache = new WeakMap();

const getRootParams = (paramsState) => {
  if (!paramsState || typeof paramsState !== 'object') {
    return createDefaultParams();
  }

  const cached = rootParamsSelectorCache.get(paramsState);
  if (cached) {
    return cached;
  }

  const { scoped, ...rootParams } = paramsState;
  rootParamsSelectorCache.set(paramsState, rootParams);
  return rootParams;
};

const getTarget = (state, key) => {
  // only treat null or undefined as root
  if (key == null) {
    return state;
  }

  if (!state.scoped) {
    state.scoped = {};
  }

  if (!state.scoped[key]) {
    state.scoped[key] = createDefaultParams();
  }

  return state.scoped[key];
};

const slice = createSlice({
  name: 'params',
  initialState,

  reducers: {
    filterAdded: (state, action) => {
      const payload = action.payload || {};
      const key = payload.key;
      const target = getTarget(state, key);

      // OLD style:
      // dispatch(filterAdded({ field: value }))
      if (!payload.filter) {
        target.filter = { ...payload };
      } else {
        // NEW style:
        // dispatch(filterAdded({ key, filter: {...} }))
        target.filter = { ...payload.filter };
      }

      target.skip = 0;
      // Bumped on every Apply/search so screens can force a fresh fetch for
      // identical params (see LIST_REFETCH_ON_REAPPLY / withReapplyRefresh).
      target.refreshTick = (target.refreshTick || 0) + 1;
    },

    resetFilter: (state, action) => {
      const key = action.payload?.key;
      const target = getTarget(state, key);
      Object.assign(target, createDefaultParams());
    },

    paginationAdded: {
      reducer: (state, action) => {
        const payload = action.payload || {};
        const key = payload.key;
        const target = getTarget(state, key);

        target.skip = payload.skip;
        target.limit = payload.limit;
      },
      prepare: (payload) => {
        setSavedFilter({ paginations: payload });
        return { payload };
      },
    },

    // ⚠️ Keep old misspelled name for backward compatibility
    fieldspAdded: (state, action) => {
      const payload = action.payload || {};
      const key = payload.key;
      const target = getTarget(state, key);

      target.fields = payload.fields;
    },

    // Optional corrected alias (safe addition)
    fieldsAdded: (state, action) => {
      const payload = action.payload || {};
      const key = payload.key;
      const target = getTarget(state, key);

      target.fields = payload.fields;
    },

    sortChanged: (state, action) => {
      const payload = action.payload || {};
      const key = payload.key;
      const target = getTarget(state, key);

      target.sort = payload.sort;
    },

    paramsReseted: (state, action) => {
      const key = action.payload?.key;
      const target = getTarget(state, key);
      Object.assign(target, createDefaultParams());
      // Bump so a fresh entry to the list refetches even when the reset params
      // equal the cached ones (e.g. returning after creating a record). Gated by
      // LIST_REFETCH_ON_REAPPLY at the query-arg layer.
      target.refreshTick = (target.refreshTick || 0) + 1;
    },

    resetAllScopedParams: (state) => {
      state.scoped = {};
    },

    removeParamsKey: (state, action) => {
      const key = action.payload?.key;
      if (key != null && state.scoped?.[key]) {
        delete state.scoped[key];
      }
    },
  },
});

//
// ================= SELECTORS =================
//

/**
 * Root params selector (old style)
 * useSelector(state => state.params)
 * still works — no change needed
 */

/**
 * Safe selector for both root & scoped
 *
 * `selectParams(key)` is called inline inside `useSelector(...)`, so it MUST return the SAME
 * selector instance for a given key across renders. Building a fresh `createSelector` per render
 * gives it an empty memo cache, so it returns a new object every render — under react-redux 9
 * (useSyncExternalStore) that reads as a changed value every render and triggers an infinite
 * re-render loop ("Maximum update depth exceeded"). react-redux 7 tolerated this; 9 does not.
 * See https://github.com/reduxjs/redux-toolkit/issues/4257
 */
const paramSelectorCache = new Map();
export const selectParams = (key) => {
  const cacheKey = key == null ? '__root__' : key;
  let selector = paramSelectorCache.get(cacheKey);
  if (!selector) {
    selector = createSelector(
      (state) => state.params,
      (paramsState) => {
        if (key == null) {
          return getRootParams(paramsState);
        }

        return paramsState?.scoped?.[key] || createDefaultParams();
      },
    );
    paramSelectorCache.set(cacheKey, selector);
  }
  return selector;
};

/**
 * Backward compatible isFiltered
 *
 * Supports:
 * useSelector(isFiltered)
 * useSelector(isFiltered())
 * useSelector(isFiltered('key'))
 */
export const isFiltered = (stateOrKey) => {
  // Used directly as selector
  if (typeof stateOrKey === 'object') {
    const state = stateOrKey;
    const paramsState = state.params;

    return Object.keys(paramsState.filter || {}).length > 0;
  }

  const key = stateOrKey;

  return createSelector(
    (state) => state.params,
    (paramsState) => {
      const target = key == null ? paramsState : paramsState?.scoped?.[key];

      if (!target) {
        return false;
      }

      return Object.keys(target.filter || {}).length > 0;
    },
  );
};

/**
 * Backward compatible isDefault
 */
export const isDefault = (stateOrKey) => {
  if (typeof stateOrKey === 'object') {
    const state = stateOrKey;
    const paramsState = state.params;

    return (
      !Object.keys(paramsState.filter || {}).length &&
      paramsState.skip === 0 &&
      paramsState.limit === defaultLimit &&
      !paramsState.fields &&
      !paramsState.sort
    );
  }

  const key = stateOrKey;

  return createSelector(
    (state) => state.params,
    (paramsState) => {
      const target = key == null ? paramsState : paramsState?.scoped?.[key];

      if (!target) {
        return true;
      }

      return (
        !Object.keys(target.filter || {}).length &&
        target.skip === 0 &&
        target.limit === defaultLimit &&
        !target.fields &&
        !target.sort
      );
    },
  );
};

export const getCurrentParams = (state, key) => {
  const rootParams = state?.params;

  if (!rootParams) {
    return createDefaultParams();
  }

  const scoped = rootParams.scoped ?? {};

  const activeParams =
    key != null ? scoped[key] ?? createDefaultParams() : rootParams;

  return {
    filter: { ...activeParams.filter },
    skip: activeParams.skip ?? 0,
    limit: activeParams.limit ?? defaultLimit,
    fields: activeParams.fields ?? '',
    sort: activeParams.sort ?? '',
  };
};

export const {
  filterAdded,
  paginationAdded,
  fieldspAdded, // old export preserved
  fieldsAdded, // optional new alias
  paramsReseted,
  resetAllScopedParams,
  sortChanged,
  resetFilter,
  removeParamsKey,
} = slice.actions;

export default slice.reducer;
